import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { track } from '@gonfalon/analytics/track';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Pagination } from '@gonfalon/pagination/component';
import { Alert, Focusable, ProgressBar, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { DEFAULT_USAGE_PRECISION } from '../../../../constants';
import type { AggregationType, Granularity, MeterType, UsageSeriesList, UsageTabKey } from '../../../../types';
import { DimensionType } from '../../../../types';
import { V1_SDK_METADATA_KEYS } from '../../../constants/metricDimensions';
import { useGroupedUsageTabData } from '../../../hooks/useGroupedUsageTabData';
import type { useUsageTimestamps } from '../../../hooks/useUsageTimestamps';
import { getEffectiveTo } from '../../../utils/dates';
import { getMaxYValue, UsageDetailChart } from '../../charts/UsageDetailChart';

import styles from './UsageDetailGrouped.module.css';

export const PAGE_SIZE = 15;
export const MIN_DURATION_MS = 200;

const DATA_EXPORT_DESTINATION_LABELS: Record<string, string> = {
  kinesis: 'Amazon Kinesis',
  'google-pubsub': 'Google Cloud Pub/Sub',
  mparticle: 'mParticle',
  segment: 'Segment',
  'azure-event-hubs': 'Azure Event Hubs',
  snowflake: 'Snowflake (legacy streaming)',
  'snowflake-v2': 'Snowflake',
  databricks: 'Databricks',
  bigquery: 'BigQuery',
  redshift: 'Redshift',
  clickhouse: 'ClickHouse',
  s3: 'S3',
};

// Helper function to track synchronous operation duration
function trackDuration<T extends unknown[]>(fn: () => T, itemsCount: number): T {
  const timeStart = Date.now();
  const result = fn();
  const timeEnd = Date.now();
  const duration = timeEnd - timeStart;

  if (duration > MIN_DURATION_MS) {
    // Track the duration using analytics
    track('Filter Dimensions by Text Duration', 'Plan Usage', {
      inputCount: itemsCount,
      resultCount: result.length,
      durationMs: duration,
    });
  }
  return result;
}

// Reusable empty state component
function EmptyState({ message = msg('billing.usageBreakdown.noDimensionMatches') }: { message?: string }) {
  return (
    <div className={styles.emptyStateWrapper}>
      <p className={styles.emptyStateDescription}>{message}</p>
    </div>
  );
}

type UsageDetailGroupedProps = {
  usageTab: UsageTabKey;
  aggregationType: AggregationType;
  timestamps: ReturnType<typeof useUsageTimestamps>;
  today: Date;
  dimension: DimensionType;
  meterType: MeterType;
  abbreviatedName: string;
  limitValue: number;
  granularity: Granularity;
  search: string;
  onHasResultsChange: (hasResults: boolean) => void;
  precision?: number;
  trialEndUTC?: number;
};

function UsageDetailGroupedContent({
  usageTab,
  aggregationType,
  timestamps,
  today,
  dimension,
  meterType,
  abbreviatedName,
  limitValue,
  granularity,
  search,
  onHasResultsChange,
  precision = DEFAULT_USAGE_PRECISION,
  trialEndUTC,
}: UsageDetailGroupedProps) {
  const [page, setPage] = useState(0);
  const effectiveTo = getEffectiveTo(timestamps.to, today, aggregationType);

  const { data, isLoading, isError, sortedTotals, metadataKey } = useGroupedUsageTabData({
    usageTab,
    fromTimestampUTC: timestamps.from,
    toTimestampUTC: effectiveTo,
    aggregationType,
    dimension,
    granularity,
  });

  const metadata = data?.metadata;
  const precomputedTitles = useMemo(() => {
    if (!metadata) {
      return {};
    }

    const titles: Record<string, string> = {};
    metadata.forEach((item, idx) => {
      titles[idx.toString()] = getGroupedTitle({
        dimension,
        metadataKey,
        metadata: item,
      });
    });
    return titles;
  }, [metadata, dimension, metadataKey]);

  // Calculate if there are results available
  const hasResults = useMemo(
    () => !isLoading && !isError && data && sortedTotals && sortedTotals.length > 0,
    [isLoading, isError, data, sortedTotals],
  );

  // Filter the data based on search term with duration tracking
  const filteredTotals = useMemo(() => {
    if (!sortedTotals?.length || !search.trim()) {
      return sortedTotals || [];
    }

    return trackDuration(
      () =>
        sortedTotals.filter(([, idx]) => {
          const title = precomputedTitles[idx];
          return title?.toLowerCase().includes(search.toLowerCase()) ?? false;
        }),
      sortedTotals?.length || 0,
    );
  }, [sortedTotals, search, precomputedTitles]);

  const yAxisMax = useMemo(() => getMaxYValue(data?.series ?? [], precision), [data?.series, precision]);

  const pageResetKey = {
    series: data?.series,
    dimension,
    aggregationType,
    from: timestamps.from,
    to: timestamps.to,
    search,
  };
  const [prevPageResetKey, setPrevPageResetKey] = useState(pageResetKey);
  if (
    prevPageResetKey.series !== pageResetKey.series ||
    prevPageResetKey.dimension !== pageResetKey.dimension ||
    prevPageResetKey.aggregationType !== pageResetKey.aggregationType ||
    prevPageResetKey.from !== pageResetKey.from ||
    prevPageResetKey.to !== pageResetKey.to ||
    prevPageResetKey.search !== pageResetKey.search
  ) {
    setPrevPageResetKey(pageResetKey);
    setPage(0);
  }

  useEffect(() => {
    onHasResultsChange(hasResults ?? false);
  }, [hasResults, onHasResultsChange]);

  if (isLoading) {
    return (
      <div>
        <ProgressBar aria-label={msg('billing.usageBreakdown.loadingLabel')} isIndeterminate size="medium" />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <Alert status="error">
          <Message id="billing.usageBreakdown.errorLoadingDimension" />
        </Alert>
      </div>
    );
  }

  if (!data || !filteredTotals.length) {
    if (search.trim() && hasResults) {
      return <EmptyState message={msg('billing.usageBreakdown.noSearchMatches')} />;
    } else {
      return <EmptyState />;
    }
  }

  const totalPages = Math.ceil(filteredTotals.length / PAGE_SIZE);
  const paginatedTotals = filteredTotals.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  if (!paginatedTotals.length) {
    return <EmptyState />;
  }

  return (
    <>
      <div className={styles.cardGrid}>
        {paginatedTotals.map(([, idx]) => (
          <GroupedUsageCard
            key={idx}
            metadataIdx={Number(idx)}
            dimension={dimension}
            metadataKey={metadataKey}
            data={data}
            aggregationType={aggregationType}
            timestamps={timestamps}
            abbreviatedName={abbreviatedName}
            limitValue={limitValue}
            meterType={meterType}
            yAxisMax={yAxisMax}
            granularity={granularity}
            precomputedTitle={precomputedTitles[idx]}
            precision={precision}
            trialEndUTC={trialEndUTC}
          />
        ))}
      </div>
      {totalPages > 1 && (
        <Pagination
          className={styles.paginateContainer}
          from={page * PAGE_SIZE + 1}
          to={Math.min((page + 1) * PAGE_SIZE, filteredTotals.length)}
          total={filteredTotals.length}
          onFirstClick={() => setPage(0)}
          onPreviousClick={() => setPage((p) => Math.max(0, p - 1))}
          onNextClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          onLastClick={() => setPage(totalPages - 1)}
        />
      )}
    </>
  );
}

export type GroupedUsageCardProps = {
  metadataIdx: number;
  dimension: DimensionType;
  metadataKey: string;
  data: UsageSeriesList;
  aggregationType: AggregationType;
  timestamps: ReturnType<typeof useUsageTimestamps>;
  abbreviatedName: string;
  limitValue: number;
  meterType: MeterType;
  yAxisMax: number;
  granularity: Granularity;
  precomputedTitle?: string;
  precision?: number;
  trialEndUTC?: number;
  seriesColor?: string;
  isTitleFocusable?: boolean;
  titleAction?: ReactNode;
};

export function GroupedUsageCard({
  metadataIdx,
  dimension,
  metadataKey,
  data,
  aggregationType,
  timestamps,
  abbreviatedName,
  limitValue,
  meterType,
  yAxisMax,
  granularity,
  precomputedTitle,
  precision = DEFAULT_USAGE_PRECISION,
  trialEndUTC,
  seriesColor,
  isTitleFocusable = true,
  titleAction,
}: GroupedUsageCardProps) {
  const title = precomputedTitle ?? getGroupedTitle({ dimension, metadataKey, metadata: data.metadata[metadataIdx] });

  const titleElement = isTitleFocusable ? (
    <TooltipTrigger>
      <Focusable>
        <span className={styles.chartTitle} role="button">
          {title}
        </span>
      </Focusable>
      {!!title && <Tooltip placement="top left">{title}</Tooltip>}
    </TooltipTrigger>
  ) : (
    <span className={styles.chartTitle}>{title}</span>
  );

  return (
    <div className={styles.chartWrapper}>
      {titleAction ? (
        <div className={styles.titleRow}>
          {titleElement}
          {titleAction}
        </div>
      ) : (
        titleElement
      )}
      <UsageDetailChart
        size="small"
        key={metadataIdx}
        metaDataIdx={metadataIdx}
        data={data}
        aggregationType={aggregationType}
        cutoffTimestampUTC={timestamps.cutoff}
        fromTimestampUTC={timestamps.from}
        toTimestampUTC={timestamps.to}
        abbreviatedName={abbreviatedName}
        limitValue={limitValue}
        meterType={meterType}
        yAxisMax={yAxisMax}
        granularity={granularity}
        precision={precision}
        trialEndUTC={trialEndUTC}
        seriesColor={seriesColor}
      />
    </div>
  );
}

export function UsageDetailGrouped(props: UsageDetailGroupedProps) {
  return (
    <ErrorBoundary
      severity="medium"
      fallbackRender={() => (
        <Alert status="error">
          <Message id="billing.usageBreakdown.errorLoadingDimension" />
        </Alert>
      )}
    >
      <UsageDetailGroupedContent {...props} />
    </ErrorBoundary>
  );
}

type GetTitleProps = {
  dimension: DimensionType;
  metadataKey: string;
  metadata?: UsageSeriesList['metadata'][number];
};

export function getGroupedTitle({ dimension, metadataKey, metadata }: GetTitleProps): string {
  const dimensionId = metadata?.[metadataKey];

  if (dimensionId === '') {
    return '<empty string>';
  }
  if (dimensionId === null) {
    return '<null>';
  }
  if (dimensionId === undefined) {
    return msg('billing.usageBreakdown.unknown');
  }
  if (typeof dimensionId !== 'string') {
    return msg('billing.usageBreakdown.invalidType');
  }

  if (dimension === DimensionType.ANONYMOUS_V2) {
    return dimensionId === 'true'
      ? msg('billing.usageBreakdown.anonymous')
      : dimensionId === 'false'
        ? msg('billing.usageBreakdown.notAnonymous')
        : dimensionId;
  }

  if (dimension === DimensionType.PROJECT_ID || dimension === DimensionType.ENVIRONMENT_ID) {
    const rootKey = getRootKey(dimension);
    const nameKey = `${rootKey}Name`;
    const dimensionName = metadata?.[nameKey];

    if (dimension === DimensionType.ENVIRONMENT_ID && dimensionName && metadata?.projectName) {
      return `${metadata.projectName} - ${dimensionName}`;
    }
    return (dimensionName as string) ?? dimensionId;
  }

  if (dimension === DimensionType.DESTINATION || dimension === DimensionType.DESTINATION_KIND) {
    return DATA_EXPORT_DESTINATION_LABELS[dimensionId] ?? dimensionId;
  }

  if (dimension === DimensionType.DESTINATION_ID) {
    return typeof metadata?.destinationName === 'string' ? metadata.destinationName : dimensionId;
  }

  if (dimension === DimensionType.SDK_VERSION || dimension === DimensionType.SDK_VERSION_V1) {
    const sdk = dimension === DimensionType.SDK_VERSION ? metadata?.sdkName : metadata?.[V1_SDK_METADATA_KEYS.sdk];
    const version =
      dimension === DimensionType.SDK_VERSION ? metadata?.sdkVersion : metadata?.[V1_SDK_METADATA_KEYS.version];

    if (sdk && typeof sdk === 'string') {
      const sdkName = sdk === 'Unknown' ? msg('billing.usageBreakdown.unknownSdk') : sdk;
      if (version && typeof version === 'string') {
        const versionName = version === 'Unknown' ? msg('billing.usageBreakdown.unknownVersion') : version;
        return `${sdkName} - ${versionName}`;
      }
      return sdkName;
    }
  }

  return dimensionId;
}

function getRootKey(dimension: DimensionType): string {
  return dimension.slice(0, -2);
}
